// frontend/src/pages/TemplateAdminPage.js
// CORREGIDO: Lógica de renderizado para mostrar TODOS los documentos requeridos.

import React, { useEffect, useState, useMemo } from 'react';
import { useTemplateStore } from '../store/templateStore';
import LoadingSpinner from '../components/LoadingSpinner';
import CustomConfirmDialog from '../components/CustomConfirmDialog';
import { PhaseModal, TaskModal, DocumentSlotModal } from '../components/TemplateEditModals';
import {
  ArrowLeftIcon,
  WrenchScrewdriverIcon,
  PencilIcon,
  TrashIcon,
  PlusCircleIcon,
  DocumentDuplicateIcon,
  ListBulletIcon,
  TagIcon
} from '@heroicons/react/24/solid';

function TemplateAdminPage({ onBack }) {
    const {
        fullConfiguration,
        isLoading,
        fetchFullTemplateConfiguration,
        addPhase, updatePhase, deletePhase,
        addTask, updateTask, deleteTask,
        addDocumentSlot, updateDocumentSlot, deleteDocumentSlot,
    } = useTemplateStore();

    const [activeTabId, setActiveTabId] = useState(null);
    const [modalState, setModalState] = useState({ type: null, data: null });
    const [confirmState, setConfirmState] = useState({ show: false, onConfirm: null });

    useEffect(() => {
        fetchFullTemplateConfiguration();
    }, [fetchFullTemplateConfiguration]);

    useEffect(() => {
        if (fullConfiguration.length > 0 && !activeTabId) {
            setActiveTabId(fullConfiguration[0].id);
        }
    }, [fullConfiguration, activeTabId]);

    const selectedInitiative = useMemo(() => 
        fullConfiguration.find(item => item.id === activeTabId),
    [fullConfiguration, activeTabId]);
    
    const selectedTemplate = selectedInitiative?.templates[0];

    // --- LÓGICA CORREGIDA ---
    // Crea una lista unificada de todos los documentos: los generales y los de cada fase.
    const allDocumentSlots = useMemo(() => {
        if (!selectedInitiative) return [];
    
        // 1. Obtiene los documentos generales (sin fase)
        const generalSlots = selectedInitiative.documentSlots || [];
        
        // 2. Obtiene los documentos de cada fase y les añade una propiedad 'phaseName'
        const phaseSlots = selectedInitiative.templates?.[0]?.phases.flatMap(phase => 
            (phase.documentSlots || []).map(slot => ({ ...slot, phaseName: phase.name }))
        ) || [];
        
        // 3. Combina ambas listas y las ordena
        const combined = [...generalSlots, ...phaseSlots];
        combined.sort((a, b) => a.order - b.order);
        return combined;
    }, [selectedInitiative]);

    const handleOpenModal = (type, data = {}) => {
        setModalState({ type, data });
    };

    const handleCloseModal = () => {
        setModalState({ type: null, data: null });
    };

    const handleDeleteClick = (onConfirmAction) => {
        setConfirmState({ show: true, onConfirm: onConfirmAction });
    };

    const handleConfirmDelete = () => {
        if (confirmState.onConfirm) {
            confirmState.onConfirm();
        }
        setConfirmState({ show: false, onConfirm: null });
    };

    const handleSubmit = async (type, formData) => {
        try {
            switch (type) {
                case 'phase':
                    if (formData.id) await updatePhase(formData.id, formData);
                    else await addPhase(selectedTemplate.id, formData);
                    break;
                case 'task':
                    if (formData.id) await updateTask(formData.id, formData);
                    else await addTask(modalState.data.phaseId, formData);
                    break;
                case 'document':
                    if (formData.id) await updateDocumentSlot(formData.id, formData);
                    else await addDocumentSlot(selectedInitiative.id, formData);
                    break;
                default:
                    break;
            }
            handleCloseModal();
        } catch (error) {
            // El error ya se muestra en la UI a través del store
        }
    };

    return (
        <div className="p-6 bg-white rounded-lg shadow-xl w-full max-w-6xl mx-auto my-8">
            <div className="flex justify-between items-center mb-6 border-b pb-4">
                <h2 className="text-3xl font-extrabold text-gray-900 flex items-center">
                    <WrenchScrewdriverIcon className="h-8 w-8 mr-3 text-blue-600" />
                    Gestión de Plantillas de Iniciativas
                </h2>
                <button onClick={onBack} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md shadow-sm hover:bg-gray-300"><ArrowLeftIcon className="h-5 w-5 mr-2 inline-block" />Volver</button>
            </div>

            {isLoading && !selectedInitiative ? (
                <div className="flex justify-center items-center h-64"><LoadingSpinner size="lg" /></div>
            ) : (
                <div>
                    <div className="border-b border-gray-200 mb-6">
                        <nav className="-mb-px flex space-x-6" aria-label="Tabs">
                            {fullConfiguration.map((initiative) => (
                                <button key={initiative.id} onClick={() => setActiveTabId(initiative.id)} className={`${activeTabId === initiative.id ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}>
                                    {initiative.name}
                                </button>
                            ))}
                        </nav>
                    </div>

                    {selectedInitiative && selectedTemplate && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            {/* Columna de Fases y Tareas */}
                            <div className="space-y-6">
                                <div className="flex justify-between items-center">
                                    <h3 className="text-xl font-bold text-gray-900 flex items-center"><ListBulletIcon className="h-6 w-6 mr-2" />Fases y Tareas</h3>
                                    <button onClick={() => handleOpenModal('phase', { name: '', order: (selectedTemplate.phases.length + 1) * 10 })} className="px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center space-x-2 text-sm"><PlusCircleIcon className="h-5 w-5" /><span>Añadir Fase</span></button>
                                </div>
                                {selectedTemplate.phases.map(phase => (
                                    <div key={phase.id} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                        <div className="flex justify-between items-center mb-3">
                                            <h4 className="font-semibold text-gray-800">{phase.name}</h4>
                                            <div className="flex items-center space-x-2">
                                                <button onClick={() => handleOpenModal('phase', phase)} className="text-indigo-600 hover:text-indigo-900 p-1"><PencilIcon className="h-5 w-5" /></button>
                                                <button onClick={() => handleDeleteClick(() => deletePhase(phase.id))} className="text-red-600 hover:text-red-900 p-1"><TrashIcon className="h-5 w-5" /></button>
                                            </div>
                                        </div>
                                        <ul className="space-y-2">
                                            {phase.tasks.map(task => (
                                                <li key={task.id} className="flex justify-between items-center p-2 rounded-md hover:bg-gray-100 text-sm">
                                                    <span className="text-gray-700">{task.description}</span>
                                                    <div className="flex items-center space-x-2">
                                                        <button onClick={() => handleOpenModal('task', { ...task, phaseId: phase.id })} className="text-indigo-600 hover:text-indigo-900 p-1"><PencilIcon className="h-4 w-4" /></button>
                                                        <button onClick={() => handleDeleteClick(() => deleteTask(task.id))} className="text-red-600 hover:text-red-900 p-1"><TrashIcon className="h-4 w-4" /></button>
                                                    </div>
                                                </li>
                                            ))}
                                            <li>
                                                <button onClick={() => handleOpenModal('task', { phaseId: phase.id, description: '', order: (phase.tasks.length + 1) * 10 })} className="w-full mt-2 text-sm text-blue-500 hover:text-blue-700 flex items-center justify-center p-2 rounded-md border-2 border-dashed border-gray-300 hover:border-blue-400 transition-colors">
                                                    <PlusCircleIcon className="h-5 w-5 mr-2" />Añadir Tarea
                                                </button>
                                            </li>
                                        </ul>
                                    </div>
                                ))}
                            </div>

                            {/* Columna de Documentos Requeridos */}
                            <div className="space-y-6">
                                <div className="flex justify-between items-center">
                                    <h3 className="text-xl font-bold text-gray-900 flex items-center"><DocumentDuplicateIcon className="h-6 w-6 mr-2" />Documentos Requeridos</h3>
                                    <button onClick={() => handleOpenModal('document', { name: '', key: '', order: (allDocumentSlots.length + 1) * 10, templatePhaseId: null })} className="px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center space-x-2 text-sm"><PlusCircleIcon className="h-5 w-5" /><span>Añadir Documento</span></button>
                                </div>
                                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                    <ul className="space-y-2">
                                        {/* --- RENDERIZADO CORREGIDO --- */}
                                        {allDocumentSlots.map(slot => (
                                            <li key={slot.id} className="flex justify-between items-center p-2 rounded-md hover:bg-gray-100 text-sm">
                                                <div className="flex items-center">
                                                    <TagIcon className="h-4 w-4 mr-2 text-gray-400" />
                                                    <span className="text-gray-700">{slot.name}</span>
                                                    {slot.phaseName && (
                                                        <span className="ml-2 text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">{slot.phaseName}</span>
                                                    )}
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <button onClick={() => handleOpenModal('document', slot)} className="text-indigo-600 hover:text-indigo-900 p-1"><PencilIcon className="h-4 w-4" /></button>
                                                    <button onClick={() => handleDeleteClick(() => deleteDocumentSlot(slot.id))} className="text-red-600 hover:text-red-900 p-1"><TrashIcon className="h-4 w-4" /></button>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {modalState.type === 'phase' && (
                <PhaseModal 
                    show={true} 
                    onClose={handleCloseModal} 
                    onSubmit={(data) => handleSubmit('phase', data)} 
                    initialData={modalState.data} 
                />
            )}
            {modalState.type === 'task' && (
                <TaskModal 
                    show={true} 
                    onClose={handleCloseModal} 
                    onSubmit={(data) => handleSubmit('task', data)} 
                    initialData={modalState.data} 
                />
            )}
            {modalState.type === 'document' && (
                <DocumentSlotModal 
                    show={true} 
                    onClose={handleCloseModal} 
                    onSubmit={(data) => handleSubmit('document', data)} 
                    initialData={modalState.data} 
                    phases={selectedTemplate?.phases || []} 
                />
            )}
            
            <CustomConfirmDialog show={confirmState.show} message="¿Estás seguro? Esta acción no se puede deshacer." onConfirm={handleConfirmDelete} onCancel={() => setConfirmState({ show: false, onConfirm: null })} />
        </div>
    );
}

export default TemplateAdminPage;
